import { Avatar, Col, Icon, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, QUIZ_TYPE, theme } from 'lib';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import styled, { css, keyframes } from 'styled-components';

/**
 * Participants drill-down for one quiz — GET /admin/quizzes/participants. Every response is
 * one row (an answer is immutable, one per user per quiz), newest first: who answered,
 * which answer, whether it was correct, points earned and when. Header cards from the
 * aggregate summary.
 */

const PAGE_LIMIT = 50;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid ${theme.GREY_30};
  border-radius: 8px;
  background: #fff;
  color: ${theme.GREY_70};
  font-family: CeraPro;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: ${theme.SECONDARY};
    border-color: ${theme.SECONDARY};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  svg {
    ${(props) =>
      props.$spinning &&
      css`
        animation: ${spin} 0.8s linear infinite;
      `}
  }
`;

const PageButton = styled.button`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.GREY_30};
  border-radius: 8px;
  background: #fff;
  color: ${theme.GREY_70};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${theme.SECONDARY};
    border-color: ${theme.SECONDARY};
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: CeraPro;
    font-size: 12.5px;
  }

  th {
    text-align: left;
    padding: 8px 10px;
    color: ${theme.GREY_50};
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid ${theme.GREY_20};
    white-space: nowrap;
  }

  td {
    padding: 9px 10px;
    border-bottom: 1px solid ${theme.GREY_10};
    color: ${theme.GREY_80};
    white-space: nowrap;
    vertical-align: middle;
  }
`;

const AnswerChip = styled.span`
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-family: CeraPro;
  font-weight: 600;
  white-space: nowrap;
  background: ${theme.GREY_10};
  color: ${theme.GREY_70};
  border: 1px solid ${theme.GREY_20};
`;

const SummaryCard = ({ label, value, highlight }) => (
  <Col
    noFlex
    pad="12px 14px"
    style={{
      flex: '1 1 130px',
      minWidth: '120px',
      background: highlight ? theme.SECONDARY_10 : theme.GREY_10,
      borderRadius: '11px',
      border: `1px solid ${highlight ? theme.SECONDARY_20 : theme.GREY_20}`
    }}
  >
    <Text
      marg="0"
      style={{
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: highlight ? theme.SECONDARY : theme.GREY_50
      }}
    >
      {label}
    </Text>
    <Text marg="4px 0 0" style={{ fontSize: '20px', fontWeight: 800, lineHeight: 1.1 }}>
      {value ?? 0}
    </Text>
  </Col>
);

const fmt = (d, pattern = 'MMM dd, h:mm a') => (d ? format(new Date(d), pattern) : '—');

const QuizParticipantsModal = ({ visible, quiz, onClose }) => {
  const [participants, setParticipants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState(null);

  const quizId = quiz?._id;

  const fetchParticipants = useCallback(
    async (isRefetch = false) => {
      if (!quizId) return;
      if (isRefetch) setRefetching(true);
      else setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        quizId,
        page: String(page),
        limit: String(PAGE_LIMIT)
      });
      const res = await Api.get(`/admin/quizzes/participants?${params.toString()}`, null, {
        silent: true
      });
      if (res?.data) {
        setParticipants(res.data.participants || []);
        setSummary(res.data.summary || null);
        setTotal(res.data.total || 0);
      } else if (res?.err) {
        setError(res.err);
      }
      if (isRefetch) setRefetching(false);
      else setLoading(false);
    },
    [quizId, page]
  );

  useEffect(() => {
    if (visible && quizId) fetchParticipants();
  }, [visible, quizId, fetchParticipants]);

  // Restart from page 1 whenever a different quiz opens.
  useEffect(() => {
    setPage(1);
  }, [quizId]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const subtitle = quiz?.quizType === QUIZ_TYPE.HALF_TIME ? 'Half-time' : 'Daily';

  return (
    <Sidepanel visible={visible} wid={760} pad="0" closeModal={onClose}>
      <Col wid="100%" pad="0">
        <Row between center pad="18px 24px" style={{ borderBottom: `1px solid ${theme.GREY_20}` }}>
          <Col noFlex style={{ minWidth: 0 }}>
            <Text h4>Quiz participants</Text>
            <Text
              c2
              col={theme.GREY_60}
              marg="2px 0 0"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {quiz?.question || ''}
            </Text>
          </Col>
          <Icon type="close" dimensions={18} onClick={onClose} style={{ cursor: 'pointer' }} />
        </Row>

        <Col wid="100%" pad="20px 24px 40px">
          {/* Quiz context + refresh */}
          <Row center between noFlex wid="100%" marg="0 0 16px" gap="12px">
            <Text c2 col={theme.GREY_60} marg="0">
              {subtitle}
            </Text>
            <RefreshButton
              type="button"
              $spinning={refetching ? 1 : 0}
              disabled={refetching || loading}
              onClick={() => fetchParticipants(true)}
              title="Refresh"
            >
              <FiRefreshCw size={13} />
              Refresh
            </RefreshButton>
          </Row>

          {/* Summary cards */}
          <Row start gap="10px" wid="100%" marg="0 0 18px" style={{ flexWrap: 'wrap' }}>
            <SummaryCard label="Participants" value={summary?.totalParticipants} highlight />
            <SummaryCard label="Correct" value={summary?.correctCount} />
            <SummaryCard label="Points awarded" value={summary?.totalEarnedPoints} />
            <SummaryCard label="Reward per correct" value={quiz?.rewardPoints} />
          </Row>

          {error && (
            <Text c1 col={theme.SECONDARY} marg="0 0 12px" style={{ fontSize: '12.5px' }}>
              {error}
            </Text>
          )}

          {loading ? (
            <Row centerAll pad="40px 0">
              <Spinner2 />
            </Row>
          ) : !participants.length ? (
            <Col
              centerAll
              noFlex
              wid="100%"
              pad="40px 0"
              style={{
                borderRadius: '12px',
                border: `1px dashed ${theme.GREY_30}`,
                background: theme.GREY_10
              }}
            >
              <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '12.5px' }}>
                No responses for this quiz yet.
              </Text>
            </Col>
          ) : (
            <TableWrap>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Answer</th>
                    <th>Result</th>
                    <th>Points</th>
                    <th>Answered at</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map((p) => (
                    <tr key={`${p.position}-${p.user?._id || p.user?.username}`}>
                      <td style={{ fontWeight: 700, color: theme.GREY_50 }}>{p.position}</td>
                      <td>
                        <Row noFlex center>
                          <Avatar tiny user={p.user} avatar={p.user?.avatar} />
                          <Col noFlex marg="0 0 0 8px" style={{ minWidth: 0 }}>
                            <Text b2 marg="0" style={{ fontSize: '12.5px' }}>
                              {p.user?.name || p.user?.username || 'Unknown'}
                            </Text>
                            {p.user?.username && (
                              <Text c2 col={theme.GREY_50} marg="0" style={{ fontSize: '10.5px' }}>
                                @{p.user.username}
                              </Text>
                            )}
                          </Col>
                        </Row>
                      </td>
                      <td>
                        <AnswerChip title={p.answer?.text || undefined}>
                          {p.answer?.text || 'Removed answer'}
                        </AnswerChip>
                      </td>
                      <td
                        style={{
                          color: p.isCorrect ? '#0C9C74' : theme.SECONDARY,
                          fontWeight: 700
                        }}
                      >
                        {p.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                      </td>
                      <td style={{ fontWeight: 700 }}>{p.earnedPoints}</td>
                      <td style={{ color: theme.GREY_60 }}>{fmt(p.answeredAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}

          {/* Pagination */}
          {total > PAGE_LIMIT && (
            <Row center justifyContent="center" gap="14px" pad="14px 0 0" noFlex wid="100%">
              <PageButton type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <FiChevronLeft size={15} />
              </PageButton>
              <Text c1 col={theme.GREY_60} marg="0">
                Page {page} of {totalPages}
              </Text>
              <PageButton
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <FiChevronRight size={15} />
              </PageButton>
            </Row>
          )}
        </Col>
      </Col>
    </Sidepanel>
  );
};

export default QuizParticipantsModal;
